import {
  useclientStatusesSuspense,
  useViewClientsSuspense,
  type ViewClientsRequest,
  type ViewClientsResponse
} from "@swivu/client-api";
import { createContext, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { groupBy } from "../utils/groupBy.js";
import { AccordionV2 } from "./AccordionV2.js";
import { Badge } from "./Badge.js";
import ErrorBoundary from "./ErrorBoundary.js";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import { shortenUrl } from "../utils/index.js";
import { Input } from "./Input.js";
import { cn } from "../utils/cn.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import {
  GlobeAltIcon,
  MapPinIcon,
  TagIcon,
  UserGroupIcon,
  FolderIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  BuildingOfficeIcon
} from "@heroicons/react/24/outline";
import { Tooltip } from "./Tooltip.js";
import { tileForId } from "../utils/tilePalette.js";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type ViewClientsVariables = ViewClientsRequest & {
  id: string;
  boardId: number;
  clientDetailBaseUrl?: string;
};

type BaseClient = ViewClientsResponse[number];

type TransformedClient = Omit<BaseClient, "status"> & {
  status: string | undefined;
};

type ViewClientsProps = ViewClientsVariables & {
  clients: BaseClient[];
};

type ViewClientsPropsWithTransformedClients = Omit<
  ViewClientsProps,
  "clients"
> & {
  clients: TransformedClient[];
};

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const ViewClientsContext =
  createContext<ViewClientsPropsWithTransformedClients | null>(null);

const useViewClients = () => {
  const context = useContext(ViewClientsContext);
  if (!context) {
    throw new Error("useViewClients must be used within a ViewClientsProvider");
  }
  return context;
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

const ViewClientsProviderWrapper = ({
  children,
  ...props
}: ViewClientsVariables & { children: React.ReactNode }) => (
  <SuspenseWithDelay fallback={<ViewClientsSkeleton />}>
    <ErrorBoundary>
      <ViewClientsProvider {...props}>{children}</ViewClientsProvider>
    </ErrorBoundary>
  </SuspenseWithDelay>
);

const ViewClientsProvider = ({
  children,
  ...props
}: ViewClientsVariables & { children: React.ReactNode }) => {
  const { data: clients } = useViewClientsSuspense({ variables: { ...props } });
  const { data: clientStatuses } = useclientStatusesSuspense({
    variables: { id: props.boardId }
  });

  const transformedClients: TransformedClient[] = clients.map((client) => {
    const status = clientStatuses.find((s) => s.id === client.status);
    return { ...client, status: status?.value };
  });

  return (
    <ViewClientsContext.Provider
      value={{ ...props, clients: transformedClients }}
    >
      {children}
    </ViewClientsContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook: useClientSearch
// ─────────────────────────────────────────────────────────────────────────────

function useClientSearch(clients: TransformedClient[]) {
  const [query, setQuery] = useState("");

  const filteredClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const haystack = [
        c.name,
        c.website,
        c.address,
        c.city,
        c.category,
        c.status,
        c.team,
        c.aging,
        ...(c.tag ?? [])
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [clients, query]);

  return { query, setQuery, filteredClients };
}

// ─────────────────────────────────────────────────────────────────────────────
// UI Components
// ─────────────────────────────────────────────────────────────────────────────

function hasGroupValue<T extends { _group_value: unknown }>(
  result: T
): result is T & { _group_value: string } {
  return typeof result._group_value === "string";
}

type SearchBarProps = {
  search: ReturnType<typeof useClientSearch>;
  totalCount: number;
};

const SearchBar = ({ search, totalCount }: SearchBarProps) => {
  const isGummy = useIsGummy();
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative w-full sm:max-w-md">
        <MagnifyingGlassIcon
          className={cn(
            "pointer-events-none absolute top-1/2 -translate-y-1/2",
            isGummy
              ? "left-5 h-7 w-7 text-mono-500 dark:text-mono-400"
              : "mobile-pref-control-icon left-3 h-5 w-5 text-mono-400"
          )}
        />
        {isGummy ? (
          <input
            type="text"
            value={search.query}
            onChange={(e) => search.setQuery(e.target.value)}
            placeholder="Search clients…"
            aria-label="Search clients"
            className={cn(
              "mobile-pref-command-input w-full h-14 rounded-surface-md pl-16 pr-4 outline-none transition-shadow",
              "font-display font-black text-base",
              "bg-white text-mono-900 placeholder:text-mono-400",
              "dark:bg-mono-900 dark:text-mono-100 dark:placeholder:text-mono-500",
              "focus-visible:ring-4 focus-visible:ring-primary-400/40",
              "shadow-sunken",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          />
        ) : (
          <Input
            value={search.query}
            onChange={(e) => search.setQuery(e.target.value)}
            placeholder="Search clients…"
            className="mobile-pref-command-input pl-10"
            aria-label="Search clients"
          />
        )}
      </div>
      <div
        className={cn(
          "mobile-pref-card-meta",
          isGummy
            ? "font-display font-black text-mono-700 dark:text-mono-200 text-sm tracking-wide uppercase"
            : "text-mono-500 dark:text-mono-400 text-sm"
        )}
      >
        {search.query.trim()
          ? `Showing ${search.filteredClients.length} of ${totalCount}`
          : `${totalCount} client${totalCount === 1 ? "" : "s"}`}
      </div>
    </div>
  );
};

const isActiveState = (state: string | null | undefined) =>
  state && !state.toLowerCase().startsWith("no ");

type StateIndicatorsProps = Pick<
  TransformedClient,
  "contact_state" | "checklist_state" | "reminder_state"
> & { isGummy?: boolean };

const StateIndicators = ({
  contact_state,
  checklist_state,
  reminder_state,
  isGummy = false
}: StateIndicatorsProps) => {
  if (!contact_state && !checklist_state && !reminder_state) return null;

  const activeClass = isGummy
    ? "text-white text-shadow-on-color"
    : "text-mono-600 dark:text-mono-300";
  const inactiveClass = isGummy
    ? "text-white/40"
    : "text-mono-300 dark:text-mono-600";
  const iconClass = isGummy
    ? "h-8 w-8 shrink-0"
    : "mobile-pref-detail-inline-icon";

  return (
    <div className="flex items-center gap-2">
      {contact_state && (
        <span
          className={cn(
            "inline-flex items-center",
            isActiveState(contact_state) ? activeClass : inactiveClass
          )}
        >
          <PhoneIcon className={iconClass} />
        </span>
      )}
      {checklist_state && (
        <span
          className={cn(
            "inline-flex items-center",
            isActiveState(checklist_state) ? activeClass : inactiveClass
          )}
        >
          <ClipboardDocumentCheckIcon className={iconClass} />
        </span>
      )}
      {reminder_state && (
        <span
          className={cn(
            "inline-flex items-center",
            isActiveState(reminder_state) ? activeClass : inactiveClass
          )}
        >
          <ClockIcon className={iconClass} />
        </span>
      )}
    </div>
  );
};

const EmptyState = ({
  icon: Icon,
  title,
  description
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) => {
  const isGummy = useIsGummy();
  return (
    <div
      className={cn(
        "mobile-pref-card mt-6 p-8 text-center",
        isGummy
          ? "rounded-surface-lg bg-card shadow-card dark:bg-mono-900"
          : "rounded-xl border bg-white shadow-sm dark:border-mono-700/50 dark:bg-mono-925"
      )}
    >
      <div
        className={cn(
          "relative mx-auto flex items-center justify-center overflow-hidden",
          isGummy
            ? "h-20 w-20 rounded-surface-md bg-tile-people shadow-raised-soft"
            : "h-12 w-12 rounded-2xl bg-mono-100 dark:bg-mono-800"
        )}
      >
        {isGummy && <span aria-hidden className="top-gloss" />}
        <Icon
          className={cn(
            "relative",
            isGummy
              ? "h-9 w-9 text-shadow-on-color"
              : "mobile-pref-control-icon h-5 w-5 text-mono-500"
          )}
        />
      </div>
      <div
        className={cn(
          "mobile-pref-card-title",
          isGummy
            ? "font-display font-black text-mono-900 dark:text-mono-100 mt-4 text-lg"
            : "text-mono-900 dark:text-mono-100 mt-3 text-sm font-medium"
        )}
      >
        {title}
      </div>
      <div
        className={cn(
          "mobile-pref-card-body mt-1",
          isGummy
            ? "font-bold text-mono-600 dark:text-mono-400 text-sm"
            : "text-mono-500 dark:text-mono-400 text-sm"
        )}
      >
        {description}
      </div>
    </div>
  );
};

type ClientCardProps = {
  client: TransformedClient;
  clientDetailBaseUrl: string;
  hasTeamData: boolean;
  hasTagData: boolean;
};

const ClientCard = ({
  client,
  clientDetailBaseUrl,
  hasTeamData,
  hasTagData
}: ClientCardProps) => {
  const navigate = useNavigate();
  const isGummy = useIsGummy();
  const name = client.name || `Client #${client.id}`;

  const handleClick = () => navigate(`${clientDetailBaseUrl}/${client.id}`);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const tileClass = tileForId(client.id);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "@container/clientcard group relative overflow-hidden flex flex-col h-full",
        isGummy
          ? cn(
              "rounded-surface-lg",
              tileClass,
              "shadow-card",
              "transition-transform duration-150",
              "hover:-translate-y-0.5 active:scale-[0.98]",
              "motion-reduce:transform-none motion-reduce:transition-none",
              "focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-white/80"
            )
          : cn(
              "transition-all",
              "dark:border-mono-700/50 dark:bg-mono-925 rounded-xl border border-border border-l-mono-400 dark:border-l-mono-600 bg-white shadow-sm",
              "hover:shadow-md hover:-translate-y-0.5 border-l-2",
              "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mono-300 dark:focus-visible:ring-mono-600"
            )
      )}
      aria-label={`Open ${name}`}
    >
      {isGummy && <span aria-hidden className="top-gloss" />}
      <ClientCardContent
        client={client}
        name={name}
        hasTeamData={hasTeamData}
        hasTagData={hasTagData}
        isGummy={isGummy}
      />
    </div>
  );
};

type ClientCardContentClient = Pick<
  TransformedClient,
  | "category"
  | "address"
  | "city"
  | "status"
  | "aging"
  | "contact_state"
  | "checklist_state"
  | "reminder_state"
  | "website"
  | "team"
  | "tag"
>;

type ClientCardContentProps = {
  client: ClientCardContentClient;
  name: string;
  hasTeamData: boolean;
  hasTagData: boolean;
  isGummy?: boolean;
};

const ClientCardContent = ({
  client,
  name,
  hasTeamData,
  hasTagData,
  isGummy = false
}: ClientCardContentProps) => {
  const {
    category,
    address,
    city,
    status,
    aging,
    contact_state,
    checklist_state,
    reminder_state,
    website,
    team,
    tag
  } = client;
  const hasLocation = !!address || !!city;
  const tags = (tag ?? []).filter(Boolean);
  const visibleTags = tags.slice(0, 2);
  const remainingTagCount = Math.max(tags.length - 2, 0);

  const hasMetaItems =
    category ||
    hasLocation ||
    website ||
    (hasTeamData && team) ||
    (hasTagData && visibleTags.length > 0);
  const hasStateIcons = contact_state || checklist_state || reminder_state;

  const metaItemClass = isGummy
    ? "mobile-pref-card-meta text-white text-shadow-on-color-strong font-bold inline-flex min-w-0 items-center gap-1.5 text-sm @sm/clientcard:text-base"
    : "mobile-pref-card-meta text-mono-500 dark:text-mono-400 inline-flex min-w-0 items-center gap-1 text-xs";

  const pillClass = isGummy
    ? "mobile-pref-card-meta bg-white/20 text-white text-shadow-on-color font-bold inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm @sm/clientcard:text-base"
    : "mobile-pref-card-meta border-mono-200 bg-mono-50 text-mono-700 dark:border-mono-700 dark:bg-mono-800 dark:text-mono-200 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs";

  const inlineIconClass = isGummy
    ? "h-7 w-7 shrink-0"
    : "mobile-pref-detail-inline-icon shrink-0";

  const hasAnyMetaRow = hasMetaItems || !!aging;

  return (
    <div className="mobile-pref-view-client-layout relative min-w-0 flex flex-col h-full w-full">
      <div
        className={cn(
          "min-w-0 flex-1",
          isGummy ? "p-4 @sm/clientcard:p-5" : "p-3 @sm/clientcard:p-4"
        )}
      >
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={300}>
            <Tooltip.Trigger asChild>
              <span
                className={cn(
                  "mobile-pref-card-title block min-w-0 cursor-default",
                  isGummy
                    ? "font-display font-black text-shadow-on-color-strong leading-tight text-xl @2xs/clientcard:text-2xl @sm/clientcard:text-3xl"
                    : "text-mono-900 dark:text-mono-100 text-sm font-medium"
                )}
              >
                {name}
              </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="top">{name}</Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
        {hasAnyMetaRow && (
          <div
            className={cn(
              "flex min-w-0 flex-wrap items-center",
              isGummy
                ? "mt-2 gap-x-3 gap-y-1.5 @sm/clientcard:mt-3 @sm/clientcard:gap-x-4 @sm/clientcard:gap-y-2"
                : "mt-2 gap-x-3 gap-y-1.5"
            )}
          >
            {aging && (
              <span
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5",
                  isGummy
                    ? "text-white text-shadow-on-color-strong font-black text-sm @sm/clientcard:text-base"
                    : "mobile-pref-card-meta text-mono-500 dark:text-mono-400 text-xs"
                )}
              >
                <ClockIcon className={inlineIconClass} />
                <span>{aging}</span>
              </span>
            )}
            {category && (
              <span className={metaItemClass}>
                <FolderIcon className={inlineIconClass} />
                <span className="truncate">{category}</span>
              </span>
            )}
            {hasLocation && (
              <span className={metaItemClass}>
                <MapPinIcon className={inlineIconClass} />
                <span className="truncate">
                  {[address, city].filter(Boolean).join(", ")}
                </span>
              </span>
            )}
            {website && (
              <button
                type="button"
                className={cn(
                  metaItemClass,
                  "transition-colors",
                  isGummy
                    ? "hover:text-white"
                    : "hover:text-mono-700 dark:hover:text-mono-200"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(website, "_blank", "noopener,noreferrer");
                }}
                title={website}
                aria-label={`Open website for ${name}`}
              >
                <GlobeAltIcon className={inlineIconClass} />
                <span className="truncate">{shortenUrl(website, 20)}</span>
              </button>
            )}
            {hasTeamData && team && (
              <span className={pillClass}>
                <UserGroupIcon className={inlineIconClass} />
                <span className="max-w-[10rem] truncate">{team}</span>
              </span>
            )}
            {hasTagData && visibleTags.length > 0 && (
              <>
                {visibleTags.map((t) => (
                  <span key={t} className={pillClass} title={t}>
                    <TagIcon className={inlineIconClass} />
                    <span className="max-w-[10rem] truncate">{t}</span>
                  </span>
                ))}
                {remainingTagCount > 0 && (
                  <span
                    className={cn(
                      "cursor-default",
                      isGummy
                        ? "text-white text-shadow-on-color-strong font-bold text-sm @sm/clientcard:text-base"
                        : "mobile-pref-card-meta text-mono-500 dark:text-mono-400 text-xs"
                    )}
                    title={tags.join(", ")}
                  >
                    +{remainingTagCount} more
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {hasStateIcons && (
        <div
          className={cn(
            "mt-auto flex items-center justify-between gap-4",
            isGummy
              ? "border-t border-white/20 px-4 py-2.5 @sm/clientcard:px-5 @sm/clientcard:py-3"
              : "border-t border-mono-200 dark:border-mono-700/50 px-4 py-2.5"
          )}
        >
          {status ? (
            <span
              className={cn(
                "inline-flex shrink-0 items-center",
                isGummy
                  ? "shadow-pill bg-white/95 text-mono-900 rounded-full px-3 py-1 text-sm @sm/clientcard:text-base font-black uppercase tracking-wide"
                  : "mobile-pref-card-meta rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-900/10 dark:text-blue-400 dark:ring-blue-400/20"
              )}
            >
              {status}
            </span>
          ) : (
            <span />
          )}
          <StateIndicators
            contact_state={contact_state}
            checklist_state={checklist_state}
            reminder_state={reminder_state}
            isGummy={isGummy}
          />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Components
// ─────────────────────────────────────────────────────────────────────────────

type ClientsProps = {
  clients: TransformedClient[];
  clientDetailBaseUrl?: string;
};

const Clients = ({
  clients,
  clientDetailBaseUrl = "/client"
}: ClientsProps) => {
  const search = useClientSearch(clients);
  const hasTeamData = clients.some((c) => c.team);
  const hasTagData = clients.some((c) => c.tag && c.tag.length > 0);

  const showEmpty = clients.length === 0;
  const showNoResults = !showEmpty && search.filteredClients.length === 0;

  return (
    <div className="md:px-0">
      <div className="mt-6">
        <SearchBar search={search} totalCount={clients.length} />

        {showEmpty ? (
          <EmptyState
            icon={BuildingOfficeIcon}
            title="No clients found"
            description="When clients exist for this view, they'll show up here."
          />
        ) : showNoResults ? (
          <EmptyState
            icon={MagnifyingGlassIcon}
            title="No matches"
            description="Try a different search term."
          />
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
            {search.filteredClients.map((client, i) => (
              <ClientCard
                key={`${client.id}-${i}`}
                client={client}
                clientDetailBaseUrl={clientDetailBaseUrl}
                hasTeamData={hasTeamData}
                hasTagData={hasTagData}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ViewClients = () => {
  const { clients, id, clientDetailBaseUrl = "/client" } = useViewClients();
  const isGummy = useIsGummy();

  if (clients.every((client) => !client?._group_value)) {
    return (
      <Clients clients={clients} clientDetailBaseUrl={clientDetailBaseUrl} />
    );
  }

  const clientsWithoutNull = clients
    .map((result) => ({
      ...result,
      _group_value: result._group_value ?? "undefined"
    }))
    .filter(hasGroupValue) as (TransformedClient & { _group_value: string })[];

  const groupedClients = groupBy(clientsWithoutNull, "_group_value") as Record<
    string,
    TransformedClient[]
  >;

  return (
    <AccordionV2.PersistentRoot
      persistKey={`view-clients-${id}`}
      defaultValue={["undefined"]}
    >
      {Object.entries(groupedClients).map(([key, groupClients]) => (
        <AccordionV2.Item value={key} key={key}>
          <AccordionV2.Trigger
            className="mobile-pref-view-trigger inline-flex w-full py-0"
            headerClassName="flex space-x-4 !border-b-0 py-0"
          >
            <h3
              className={cn(
                "mobile-pref-section-title dark:border-mono-700/50 flex items-center justify-between gap-4 py-2",
                isGummy
                  ? "font-display font-black text-mono-900 dark:text-mono-100 text-2xl"
                  : "text-lg"
              )}
            >
              <div className={isGummy ? "font-black" : "font-bold"}>
                <span>{key === "undefined" ? "Clients" : key}</span>
              </div>
              <Badge
                count={groupClients.length}
                action="info"
                className="mobile-pref-detail-badge static shrink-0"
              />
            </h3>
          </AccordionV2.Trigger>
          <AccordionV2.Content>
            <Clients
              clients={groupClients}
              clientDetailBaseUrl={clientDetailBaseUrl}
            />
          </AccordionV2.Content>
        </AccordionV2.Item>
      ))}
    </AccordionV2.PersistentRoot>
  );
};

const ViewClientsSkeleton = () => {
  const isGummy = useIsGummy();
  return (
    <div className="h-full w-full animate-pulse space-y-3">
      <div
        className={cn(
          "h-14 w-full",
          isGummy
            ? "rounded-surface-md bg-mono-200 dark:bg-mono-900"
            : "rounded-md bg-mono-200 dark:bg-mono-900"
        )}
      />
      {isGummy && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          <div className="rounded-surface-lg bg-mono-200 dark:bg-mono-900 h-28" />
          <div className="rounded-surface-lg bg-mono-200 dark:bg-mono-900 h-28" />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Exports
// ─────────────────────────────────────────────────────────────────────────────

ViewClients.Provider = ViewClientsProviderWrapper;
ViewClients.useViewClients = useViewClients;
ViewClients.Skeleton = ViewClientsSkeleton;
ViewClients.displayName = "ViewClients";
export default ViewClients;
